import { useState, useMemo, useEffect } from 'react';
import { InlineNotification, Loading } from '@carbon/react';
import { Anchor, Popup, useMap } from 'react-map-gl';
import classNames from 'classnames';
import { Toggle } from 'components/toggle/toggle';
import { useAtom } from 'jotai';
import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import { TypeCurveListingRow } from 'types/api-responses';

/** Shape passed from map feature click (`MapPopupData` type-curve-area). */
type TypeCurveAreaMapCurve = Pick<
  TypeCurveListingRow,
  'id' | 'uniqueTcId' | 'formation' | 'status'
>;

import baseStyle from './map.module.scss';
import typeCurveStyle from './type-curve-area-popup.module.scss';
import { TypeCurveLinksTable } from './type-curve-links-table';

interface TypeCurveHeadersQueryParams extends SearchParams {
  text: string;
  typeCurveAreaName?: string;
  active?: boolean;
}

const createTypeCurveAreaPopupSearchAtom = (areaName: string) => {
  return searchAtomPair<TypeCurveListingRow, TypeCurveHeadersQueryParams>(
    { href: '/queries/type_curve_headers' },
    {
      page: 1,
      pageSize: 1000,
      text: '',
      typeCurveAreaName: areaName,
      active: true,
      paginated: false,
      sortAttribute: 'positional_index',
      sortDirection: 'ASC',
    }
  );
};

interface TypeCurveAreaPopupProps {
  areaName: string;
  /** From map selection; list load uses `/queries/type_curve_headers` in-app. */
  typeCurves: TypeCurveAreaMapCurve[];
  latitude: number;
  longitude: number;
  closeButton: boolean;
  onClose: () => void;
}

export const TypeCurveAreaPopup = ({
  areaName,
  latitude,
  longitude,
  closeButton,
  onClose,
}: TypeCurveAreaPopupProps) => {
  // Off = show active Type Curves; on = show active + archived
  const [showArchived, setShowArchived] = useState(false);
  const [currentAreaName, setCurrentAreaName] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState(false);

  const searchAtoms = useMemo(
    () => createTypeCurveAreaPopupSearchAtom(areaName),
    [areaName]
  );
  const [searchData, triggerSearch] = useAtom(searchAtoms.searchAtom);

  const filteredTypeCurves = useMemo(() => {
    const typeCurvesData = searchData.data?.results || [];
    return typeCurvesData;
  }, [searchData.data?.results]);

  useEffect(() => {
    if (currentAreaName === areaName && searchData.data?.results?.length) {
      return;
    }

    const timeout = setTimeout(() => {
      triggerSearch(() => ({
        page: 1,
        pageSize: 1000,
        text: '',
        typeCurveAreaName: areaName,
        active: showArchived ? undefined : true,
        paginated: false,
        sortAttribute: 'positional_index',
        sortDirection: 'ASC',
      }));
      setCurrentAreaName(areaName);
      setIsExpanded(true);
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [
    areaName,
    currentAreaName,
    searchData.data?.results?.length,
    triggerSearch,
    showArchived,
  ]);

  useEffect(() => {
    if (isExpanded) {
      triggerSearch(() => ({
        page: 1,
        pageSize: 1000,
        text: '',
        typeCurveAreaName: areaName,
        active: showArchived ? undefined : true,
        paginated: false,
        sortAttribute: 'positional_index',
        sortDirection: 'ASC',
      }));
    }
  }, [showArchived, isExpanded, areaName, triggerSearch]);

  const { current: map } = useMap();
  const [anchor, setAnchor] = useState<Anchor>('right');
  const [maxWidth, setMaxWidth] = useState(700);
  const [tableHeight, setTableHeight] = useState(300);

  useEffect(() => {
    if (!map) return;

    const mapContainer = map.getContainer();
    const { width, height } = mapContainer.getBoundingClientRect();
    const point = map.project([longitude, latitude]);
    const popupWidth = 600;
    const popupHeight = 400;
    const buffer = 10;
    const spaceOnRight = width - point.x - buffer;
    const spaceOnLeft = point.x - buffer;
    const spaceOnBottom = height - point.y - buffer;
    const spaceOnTop = point.y - buffer;

    // Prefer right/bottom; when neither side fits, open toward the larger space.
    const opensRight =
      spaceOnRight >= popupWidth || spaceOnRight >= spaceOnLeft;
    const horizontalAnchor = opensRight ? 'left' : 'right';
    const horizontalSpace = opensRight ? spaceOnRight : spaceOnLeft;

    const opensDown =
      spaceOnBottom >= popupHeight || spaceOnBottom >= spaceOnTop;
    const verticalAnchor = opensDown ? 'top' : 'bottom';
    const verticalSpace = opensDown ? spaceOnBottom : spaceOnTop;

    setAnchor(`${verticalAnchor}-${horizontalAnchor}` as Anchor);
    setMaxWidth(Math.min(700, Math.max(horizontalSpace, 0)));
    // The header and padding take ~90px; the table gets the rest, up to 300px.
    setTableHeight(Math.min(300, Math.max(verticalSpace - 90, 80)));
  }, [map, longitude, latitude]);

  if (searchData.error) {
    return (
      <Popup
        latitude={latitude}
        longitude={longitude}
        closeButton={closeButton}
        closeOnClick={false}
        onClose={onClose}
        anchor={anchor}
        className={classNames(
          baseStyle.popup,
          baseStyle.titleEntityAssetsPopup
        )}
        maxWidth="600px"
      >
        <div className={baseStyle.titleEntityErrorMessage}>
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={searchData.error}
            hideCloseButton={true}
          />
        </div>
      </Popup>
    );
  }

  return (
    <Popup
      anchor={anchor}
      key={Number(closeButton)}
      closeButton={closeButton}
      closeOnClick={false}
      onClose={onClose}
      latitude={latitude}
      longitude={longitude}
      className={classNames(
        baseStyle.popup,
        baseStyle.titleEntityAssetsPopup,
        baseStyle.titleEntityAssetsPopupContainer
      )}
      maxWidth={`${maxWidth}px`}
    >
      <div className={baseStyle.popupContainer}>
        <div className={baseStyle.tabsContainer}>
          <div className={baseStyle.tabContent}>
            {!isExpanded && (
              <div>
                <p className={typeCurveStyle.entityTitle}>{areaName}</p>
              </div>
            )}

            {isExpanded && (
              <>
                <div className={typeCurveStyle.entityHeader}>
                  <div>
                    <h3 className={typeCurveStyle.entityTitle}>{areaName}</h3>
                  </div>
                  <div className={typeCurveStyle.toggleContainer}>
                    <Toggle
                      isActive={showArchived}
                      handleToggle={(visibility) => setShowArchived(visibility)}
                    />
                    <span className={typeCurveStyle.toggleLabel}>Archived</span>
                  </div>
                </div>
                {searchData.loading && (
                  <div className={typeCurveStyle.loading}>
                    <Loading small description="Loading type curves..." />
                  </div>
                )}

                {!searchData.loading && filteredTypeCurves?.length > 0 && (
                  <>
                    <TypeCurveLinksTable
                      typeCurves={filteredTypeCurves}
                      height={tableHeight}
                      fitWidth
                    />
                  </>
                )}

                {!searchData.loading &&
                  filteredTypeCurves.length === 0 &&
                  currentAreaName === areaName && (
                    <div className={typeCurveStyle.noEntitiesMessage}>
                      No type curves found for this area.
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
      </div>
    </Popup>
  );
};
